import React, { useState, useEffect, useCallback, useMemo  } from "react";
import {
  Container,
  Row,
  Col,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Button,
  Input,
  Label,
  FormGroup,
  Card,
  CardBody,
  CardTitle,
  Pagination,
  PaginationItem,
  PaginationLink,
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionBody,
  Modal,
  ModalBody
} from "reactstrap";
import { getAuthUser } from "../../../helpers/utils";
import { saveDocumentDetails, reviewedDocument } from "../../../helpers/fakebackend_helper";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import debounce from "lodash.debounce";
import * as url from "../../../helpers/url_helper";
import { updateDocumentJson } from "../../../slices/AIDoc/thunk";
import { toast } from "react-toastify";

const hideFields = {
  article_info: [],
  sections: []
};

/**
 * Recursive editor component:
 * - Renders primitive values as inputs/textareas
 * - Renders objects and arrays recursively
 * - Calls onChange(path, newValue) where path is array of keys/indexes
 */
function RecursiveEditor({ value, path = [], onChange, name, keys, readOnly = false }) {
  const { t } = useTranslation();
  const isObject = value && typeof value === "object" && !Array.isArray(value);
  const isArray = Array.isArray(value);

  const handlePrimitiveChange = (newVal) => {
    onChange(path, newVal);
  };

  if (isObject) {
    // Pick only allowed keys if `keys` is passed
    // const objectKeys = keys ? keys.filter((k) => Object.prototype.hasOwnProperty.call(value, k)) : Object.keys(value);
    const objectKeys = keys ? Object.keys(value).filter((k) => !keys.includes(k)) : Object.keys(value);

    return (
      <div style={{ borderLeft: "2px solid #eee", paddingLeft: 12, marginBottom: 8 }}>
        {name && <div style={{ fontWeight: 600, marginBottom: 6 }}>{t(name)}</div>}
        {objectKeys.map((key) => (
          <RecursiveEditor
            key={key}
            name={key}
            value={value[key]}
            path={[...path, key]}
            onChange={onChange}
            readOnly={readOnly}
          />
        ))}
      </div>
    );
  } else if (isArray) {
    return (
      <div style={{ borderLeft: "2px dashed #eee", paddingLeft: 12, marginBottom: 8 }}>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>
          {t(name)} ({t('length')}: {value.length})
        </div>
        {value.map((item, idx) => (
          <div key={idx} style={{ marginBottom: 8 }}>
            <RecursiveEditor
              name={`[${idx}]`}
              value={item}
              path={[...path, idx]}
              onChange={onChange}
              keys={keys} // pass down whitelist for nested objects
              readOnly={readOnly}
            />
            <div>
              <Button
                size="sm"
                color="danger"
                outline
                onClick={() => {
                  const newArr = [...value];
                  newArr.splice(idx, 1);
                  onChange(path, newArr);
                }}
              >
                {t('Remove')}
              </Button>
            </div>
          </div>
        ))}
        <div style={{ marginTop: 6 }}>
          <Button
            size="sm"
            color="primary"
            outline
            onClick={() => {
              const newArr = [...value, typeof value[0] === "object" ? {} : ""];
              onChange(path, newArr);
            }}
          >
            {t('Add item')}
          </Button>
        </div>
      </div>
    );
  } else {
    if (name === "validation_needs_review") {
      return (
        <>
          <FormGroup className="d-flex gap-1" style={{ marginBottom: 8 }}>
            <Label style={{ fontWeight: 600, fontSize: 14 }}>{t(name)}</Label>
            <Input
              type="checkbox"
              checked={value == true}
              onChange={(e) => handlePrimitiveChange(e.target.checked)}
              readOnly={readOnly}
            />
          </FormGroup>
        </>
      );
    }

    // primitive value
    const asString = value === null ? "" : String(value);
    const useTextarea = asString.length > 60 || asString.includes("\n") || /[اأإآ]/.test(asString);

    return (
      <FormGroup style={{ marginBottom: 8 }}>
        <Label style={{ fontWeight: 600, fontSize: 14 }}>{t(name)}</Label>
        {useTextarea ? (
          <Input
            type="textarea"
            value={asString}
            rows={5}
            onChange={(e) => handlePrimitiveChange(e.target.value)}
            readOnly={readOnly}
          />
        ) : (
          <Input
            type="text"
            value={asString}
            onChange={(e) => handlePrimitiveChange(e.target.value)}
            readOnly={readOnly}
          />
        )}
      </FormGroup>
    );
  }
}


/**
 * Helper: update nested value immutably given a path (array of keys/indexes)
 */
function updateAtPath(obj, path, newValue) {
  if (path.length === 0) {
    return newValue;
  }
  const [head, ...rest] = path;
  let clone;
  if (Array.isArray(obj)) clone = [...obj];
  else if (obj && typeof obj === "object") clone = { ...obj };
  else {
    // if path deeper but current is primitive, create object/array depending on next key
    clone = typeof head === "number" ? [] : {};
  }

  clone[head] = updateAtPath(clone[head], rest, newValue);
  return clone;
}

/**
 * Main app
 */
const ArticleDetails = () => {
  const { t, i18n } = useTranslation();
  const { user } = getAuthUser();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  let { state = {} } = useLocation();
  
  const [isSaving, setIsSaving] = useState(false);
  const [show, setShow] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [data, setData] = useState({ articles: [{}]});
  const [selectedArticleIndex, setSelectedArticleIndex] = useState(0);
  const [activeTab, setActiveTab] = useState("article");
  const selectedArticle = data.articles[selectedArticleIndex];

  // Section pagination control
  const [sectionIndex, setSectionIndex] = useState(0);
  const [openAccordion, setOpenAccordion] = useState(["1"]);

  const toggleAccordion = (id) => {
    if (openAccordion.includes(id)) {
      setOpenAccordion(openAccordion.filter((x) => x !== id));
    } else {
      setOpenAccordion([...openAccordion, id]); // multiple open allowed
    }
  };

  const onChangeValue = (path, newVal) => {
    // newVal comes as string for primitives; attempt to coerce booleans/numbers/null if appropriate
    const parsedVal = attemptParsePrimitive(newVal);
    const updated = updateAtPath(data, ["articles", selectedArticleIndex, ...path], parsedVal);
    setData(updated);

    // trigger debounced save
    if (user.RoleID == 118) {
      debouncedSave(updated);
    }
  };

  function attemptParsePrimitive(v) {
    // keep strings by default; but if user typed 'true'/'false'/'null'/'123' we might parse
    if (v === null) return null;
    if (typeof v !== "string") return v;
    const trimmed = v.trim();
    if (trimmed === "") return "";
    if (trimmed === "null") return null;
    if (trimmed === "true") return true;
    if (trimmed === "false") return false;
    // numeric?
    if (!isNaN(Number(trimmed)) && trimmed.length < 16) {
      return Number(trimmed);
    }
    return v;
  }

  // helper to update top-level fields (document_type etc.)
  const updateArticleField = (fieldPath, newValue) => {
    const updated = updateAtPath(data, ["articles", selectedArticleIndex, ...fieldPath], newValue);
    setData(updated);

    // trigger debounced save
    if (user.RoleID == 118) {
      debouncedSave(updated);
    }
  };

  // export JSON
  const exportJSON = () => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "articles_edited.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleReviewArticle = async () => {
    setShowConfirm(false);
    setIsSaving(true);
    try {
      const response = await handleSaveJson(data, true);
      await reviewedDocument({ DocID: state.DocID, IsSaved: true });
    } catch (error) {
      console.log('error: ', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveJson = async (data, pushToPinecone = false) => {
    if (isSaving) return;

    setIsSaving(true);
    try {
      data.DocID = state.DocID;
      data.JsonURL = state.JsonURL;
      data.DocumentTypeID = state.DocumentTypeID;
      data.Status = [1, 4, 98].includes(user.RoleID) ? 3 : 4;
      data.pushToPinecone = pushToPinecone;

      const formData = new FormData();
      formData.append("payload", data);

      const response = await fetch(url.SAVE_DOC_DETAILS, {
        method: "POST",
        headers: { 'Content-Type': 'application/json' },
        // headers: { 'Content-Type': "text/plain" },
        body: JSON.stringify(data), 
      });

      if (!response.body) throw new Error("No response body for stream");

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";

      // Process streaming response
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop(); // keep last incomplete chunk

        for (const eventBlock of events) {
          if (!eventBlock.trim()) continue;

          let eventName = "";
          let eventData = "";

          for (const line of eventBlock.split("\n")) {
            if (line.startsWith("event:"))
              eventName = line.slice(6).trim();
            else if (line.startsWith("data:"))
              eventData += line.slice(5).trim();
          }

          if (!eventName || !eventData) continue;

          try {
            const parsedData = JSON.parse(eventData);

            if (eventName === "file_saved") {
              // ✅ Update state safely
              if (state.JsonURL != parsedData.url) {
                state.JsonURL = parsedData.url;
                state.IsProcessed = true;
                dispatch(updateDocumentJson(state))
                navigate('/ai/article-details', { 
                  state:  {
                    ...state
                  },
                  replace: true
                })
              } else {
                toast.success(t("DocumentDetailsSavedMessage"));
              }
              return parsedData;
            }
          } catch (err) {
            console.warn("Invalid SSE event data:", eventData, err);
          }
        }
      }
    } catch (error) {
      console.error("Save stream error:", error);
    } finally {
      setIsSaving(false); // ✅ only after stream fully ends
    }
  }

  const handleDeleteArticle = () => {
    // if (!window.confirm("Are you sure you want to delete this document?")) return;

    const docs = [...data.articles];
    docs.splice(selectedArticleIndex, 1);
    setShow(false);
    if (docs.length === 0) {
      setData({ articles: [{}] }); // fallback to empty article
      setSelectedArticleIndex(0);
    } else {
      setData({ ...data, articles: docs });
      setSelectedArticleIndex(Math.max(0, selectedArticleIndex - 1));
    }

    if (user.RoleID == 118) {
      debouncedSave({ ...data, articles: docs });
    }
  };

  const debouncedSave = useCallback(
    debounce(handleSaveJson, 3000), // shorter debounce feels snappier
    [state.JsonURL] // correct deps
  );

  // Section pagination helpers
  const sections = selectedArticle?.sections || [];
  const sectionCount = sections.length;
  const currentArticle = sections[sectionIndex] || null;


  const gotoSection = (idx) => {
    const newIdx = Math.max(0, Math.min(sectionCount - 1, idx));
    setSectionIndex(newIdx);
  };

  const sectionPagination = useMemo(() => {
    const total = sectionCount;       // total pages
    const current = sectionIndex;     // current page index (0-based)
    const maxVisible = 7;             // max pages to display in window
    const pages = [];

    if (total <= 1) return [];
    // if (total <= 1) return null;

    // Always include first page
    pages.push(0);

    // Case 1: if total pages ≤ maxVisible → show all
    if (total <= maxVisible) {
      for (let i = 1; i < total; i++) {
        pages.push(i);
      }
      return pages;
    }

    // Define sliding window
    let start = Math.max(1, current - 2);
    let end = Math.min(total - 2, current + 2);

    // Ensure window always has (maxVisible - 2) slots (excluding first & last)
    const windowSize = maxVisible - 2;
    if (end - start + 1 < windowSize) {
      if (current <= Math.floor(total / 2)) {
        end = Math.min(total - 2, start + windowSize - 2);
      } else {
        start = Math.max(1, end - windowSize + 1);
      }
    }

    // Left ellipsis
    if (start > 1) {
      pages.push("ellipsis-left");
    }

    // Middle pages
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    // Right ellipsis
    if (end < total - 2) {
      pages.push("ellipsis-right");
    }

    // Always include last page
    pages.push(total - 1);

    return pages;
  }, [sectionCount, sectionIndex]);


  useEffect(() => {
    const fetchJson = async () => {
      try {
        if (!state.JsonURL) return;
        // const response = await fetch("https://storage.googleapis.com/rag-legal-docs-dev/final_output/1757680360186.json");
        const response = await fetch(state.JsonURL);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const jsonData = await response.json();
        setData(jsonData);
      } catch (err) {
        console.error("Error fetching JSON:", err);
      }
    };

    fetchJson();
  }, [state.JsonURL]);

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col>
            <Card>
              <CardBody>
                <CardTitle tag="h4">{t('Article Editor')}</CardTitle>
                <Row className="align-items-center">
                  <Col md="6">
                    <div className="d-flex gap-2 justify-content-start">
                      <Button color="secondary" onClick={() => navigate(-1)}>
                        <i className={`${i18n.language === 'en' ? "ri-arrow-left-line" : "ri-arrow-right-line"}`} />
                      </Button>
                      <UncontrolledDropdown>
                        <DropdownToggle caret>
                          {t('Article')}:
                          {selectedArticle?.article_uid
                            ? `- ${selectedArticle?.article_uid}`
                            : ""}
                        </DropdownToggle>
                        <DropdownMenu>
                          {Object.keys(data).length > 0 && data.articles.map((article, idx) => (
                            <DropdownItem
                              key={idx}
                              onClick={() => {
                                setSelectedArticleIndex(idx);
                                setSectionIndex(0);
                              }}
                            >
                              #{idx} — {article?.article_uid}
                            </DropdownItem>
                          ))}
                        </DropdownMenu>
                      </UncontrolledDropdown>
                    </div>
                  </Col>
                  <Col md="6" className="text-end">
                    <div className="d-flex gap-2 justify-content-end">
                      {/* { ([1, 4, 98].includes(user.RoleID)) && <>
                        <Button onClick={exportJSON}>
                          {t('Export JSON')}
                        </Button>
                        <Button color="success" onClick={() => handleSaveJson(data)} disabled={isSaving}>
                          {t('Save')}
                        </Button> 
                      </>
                      } */}
                      {  user.RoleID == 118 &&
                        <Button color="success" onClick={() => setShowConfirm(true)} disabled={isSaving}>
                          {t('Reviewed')}
                        </Button> 
                      }
                      {/* 🔹 New Delete Button */}
                      { data.articles?.length > 1 &&
                        <Button color="danger" onClick={() => setShow(true)}>
                          {t('Delete Article')}
                        </Button>
                      }
                    </div>
                  </Col>
                </Row>

                <Nav tabs style={{ marginTop: 16 }}>
                  <NavItem>
                    <NavLink
                      className={activeTab === "article" ? "active" : ""}
                      onClick={() => setActiveTab("article")}
                      style={{ cursor: "pointer" }}
                    >
                      {t('Article')}
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={activeTab === "section" ? "active" : ""}
                      onClick={() => setActiveTab("section")}
                      style={{ cursor: "pointer" }}
                    >
                      {t('Section')}
                    </NavLink>
                  </NavItem>
                </Nav>

                <TabContent activeTab={activeTab} style={{ marginTop: 12 }}>
                  <TabPane tabId="article">
                    <Row className="justify-content-center mt-3" style={{ height: "80vh" }}>
                      <Col md="6"
                        style={{
                          height: "100%",
                          overflowY: "auto", // vertical scrollbar for PDF
                        }}>
                        <Accordion open={openAccordion} toggle={toggleAccordion}>

                          {/* Article Info */}
                          <AccordionItem>
                            <AccordionHeader targetId="1">{t('Article Info')}</AccordionHeader>
                            <AccordionBody accordionId="1">
                              <RecursiveEditor
                                name="article_info"
                                value={selectedArticle.article_info}
                                path={["article_info"]}
                                onChange={onChangeValue} 
                              />
                            </AccordionBody>
                          </AccordionItem>

                          {/* Annexes */}
                          <AccordionItem>
                            <AccordionHeader targetId="2">{t('annexes')}</AccordionHeader>
                            <AccordionBody accordionId="2">
                              <RecursiveEditor
                                name="annexes"
                                value={selectedArticle.annexes}
                                path={["annexes"]}
                                onChange={onChangeValue} 
                              />
                            </AccordionBody>
                          </AccordionItem>
                        </Accordion>
                      </Col>
                      <Col md="6"
                        style={{
                          height: "100%",
                          overflowY: "auto",
                          overflowX: "hidden",
                        }}>
                        <iframe
                          src={state.FileURL || ''}
                          width="100%"
                          height="95%"
                          title="PDF Viewer"
                          style={{ minHeight: "570px" }}
                          ></iframe>
                      </Col>
                    </Row>
                  </TabPane>

                  <TabPane tabId="section">
                    <Row className="justify-content-center mt-3" style={{ height: "80vh" }}>
                      <Col md={6}
                        style={{
                          height: "100%",
                          overflowY: "auto", // vertical scrollbar for PDF
                        }}>
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <strong>{t('Total Articles')}</strong>{" "}
                            <small className="text-muted">({sectionCount})</small>
                          </div>
                          <div>
                            <Button
                              size="sm"
                              color="secondary"
                              outline
                              onClick={() => {
                                // add new blank section
                                const newSections = [...(selectedArticle.sections || []), { 
                                  chunk_type: "", 
                                  section_number: "",
                                  section_title_ar: "",
                                  content: ""
                                }];
                                updateArticleField(["sections"], newSections);
                                setSectionIndex(sectionCount);
                              }}
                            >
                              {t('Add Section')}
                            </Button>
                          </div>
                        </div>

                        {sectionCount === 0 ? (
                          <div style={{ marginTop: 12 }}>{t('NoArticlesAvailable')}</div>
                        ) : (
                          <>
                            <Card style={{ marginTop: 12 }}>
                              <CardBody>
                                <h6>{t('Section')} {currentArticle?.section_number}</h6>

                                {/* Dropdown to move section */}
                                <UncontrolledDropdown className="mb-3">
                                  <DropdownToggle caret>
                                    {t('Move to Article')}:
                                    {selectedArticle?.article_uid
                                      ? `- ${selectedArticle?.article_uid}`
                                      : ""}
                                  </DropdownToggle>
                                  <DropdownMenu>
                                    {data.articles.map((article, idx) => (
                                      <DropdownItem
                                        key={idx}
                                        active={idx === selectedArticleIndex}
                                        onClick={() => {
                                          if (idx === selectedArticleIndex) return; // already in same article
                                          const articleToMove = currentArticle;

                                          // remove from current article
                                          const fromDocs = [...data.articles];
                                          fromDocs[selectedArticleIndex] = {
                                            ...fromDocs[selectedArticleIndex],
                                            sections: fromDocs[selectedArticleIndex].sections.filter(
                                              (_, i) => i !== sectionIndex
                                            ),
                                          };

                                          // append into target article
                                          fromDocs[idx] = {
                                            ...fromDocs[idx],
                                            sections: [...(fromDocs[idx].sections || []), articleToMove],
                                          };

                                          // update state
                                          setData({ ...data, articles: fromDocs });
                                          if (user.RoleID == 118) {
                                            debouncedSave({ ...data, articles: fromDocs })
                                          }

                                          // reset section index
                                          setSectionIndex(Math.min(sectionIndex, fromDocs[selectedArticleIndex].sections.length - 1));
                                        }}
                                      >
                                        #{idx} — {article?.article_uid}
                                      </DropdownItem>
                                    ))}
                                  </DropdownMenu>
                                </UncontrolledDropdown>

                                <RecursiveEditor
                                  // name={`article_${sectionIndex}`}
                                  value={currentArticle}
                                  path={["sections", sectionIndex]}
                                  onChange={onChangeValue} 
                                  keys={hideFields.sections}
                                />
                              </CardBody>
                            </Card>

                            <div className="d-flex justify-content-between align-items-center" style={{ marginTop: 12 }}>
                              <Pagination>
                                <PaginationItem disabled={sectionIndex === 0}>
                                  <PaginationLink previous onClick={() => gotoSection(sectionIndex - 1)} />
                                </PaginationItem>

                                {sectionPagination.map((p, idx) =>
                                  p === "ellipsis-left" || p === "ellipsis-right" ? (
                                    <PaginationItem key={idx} disabled>
                                      <PaginationLink>...</PaginationLink>
                                    </PaginationItem>
                                  ) : (
                                    <PaginationItem active={p === sectionIndex} key={idx}>
                                      <PaginationLink onClick={() => gotoSection(p)}>
                                        {p + 1}
                                      </PaginationLink>
                                    </PaginationItem>
                                  )
                                )}


                                <PaginationItem disabled={sectionIndex === sectionCount - 1}>
                                  <PaginationLink next onClick={() => gotoSection(sectionIndex + 1)} />
                                </PaginationItem>
                              </Pagination>
                          

                              <div>
                                <Button
                                  size="sm"
                                  color="danger"
                                  outline
                                  onClick={() => {
                                    // remove current section
                                    const arr = [...selectedArticle.sections];
                                    arr.splice(sectionIndex, 1);
                                    updateArticleField(["sections"], arr);
                                    gotoSection(Math.max(0, sectionIndex - 1));
                                  }}
                                  disabled={sectionCount === 0}
                                >
                                  {t('Delete current section')}
                                </Button>
                              </div>
                            </div>
                          </>
                        )}
                      </Col>
                      <Col md="6"
                        style={{
                          height: "100%",
                          overflowY: "auto",
                          overflowX: "hidden",
                        }}>
                        <iframe
                          src={state.FileURL || ''}
                          width="100%"
                          height="95%"
                          title="PDF Viewer"
                          style={{ minHeight: "600px" }}
                          ></iframe>
                      </Col>
                    </Row>
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row> 
      </Container>

      <Modal isOpen={show} centered={true}>
        <ModalBody className="py-3 px-5">
          <div className="mt-2 text-center">
            <div className="mt-4 pt-2 fs-15 mx-4 mx-sm-5">
              <h4>{t('areYouSure')}</h4>
              <p className="text-muted mx-4 mb-0">{t('Are you sure you want to delete this document?')}</p>
            </div>
          </div>
          <div className="d-flex gap-2 justify-content-center mt-4 mb-2">
            <button
              type="button"
              className="btn w-sm btn-light"
              data-bs-dismiss="modal"
              onClick={() => setShow(false)}
            >
              {t("Close")}
            </button>
            <button
              type="button"
              className="btn w-sm btn-danger d-flex align-items-center"
              onClick={handleDeleteArticle}
            >
              {t('yesDelete')}
            </button>
          </div>
        </ModalBody>
      </Modal>

      <Modal isOpen={showConfirm} centered={true}>
        <ModalBody className="py-3 px-5">
          <div className="mt-2 text-center">
            <div className="mt-4 pt-2 fs-15 mx-4 mx-sm-5">
              <h4>{t('areYouSure')}</h4>
              <p className="text-muted mx-4 mb-0">{t('Are you sure you want to review this document?')}</p>
            </div>
          </div>
          <div className="d-flex gap-2 justify-content-center mt-4 mb-2">
            <button
              type="button"
              className="btn w-sm btn-light"
              data-bs-dismiss="modal"
              onClick={() => setShowConfirm(false)}
            >
              {t("Close")}
            </button>
            <button
              type="button"
              className="btn w-sm btn-danger d-flex align-items-center"
              disabled={isSaving}
              onClick={handleReviewArticle}
            >
              {t('Yes, Reviewed it')}
            </button>
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
}

export default ArticleDetails;